﻿/**
 * Soft≠Live Page159 — SA Vault residual liquid (page78/128 buses + status).
 * Mount: /sa/vault
 * Buses:
 *   GET /admin/backup/status (always-on Live posture)
 *   GET /admin/pg-backup/status (Page231 · PG DR survey ∥ tool honesty)
 *   GET /admin/backup/export (X-Backup-Encryption-Key)
 * Soft residual deleted:
 *  - sandpit export card without liquid rail / seat honesty / posture SoT
 *  - Soft "backed up" invent on 409 SQLITE_PHYSICAL_EOL
 *  - Soft invent "Live dump armed" when API host lacks pg_dump
 *  - Soft import CTA that pretends restore is Live (stays disabled runbook)
 */
import { useState } from "react";
import { html, F, Ico, Input, Field, Card, Alert, go } from "./ui.js";
import { api, path, useApi } from "./api.js";
import { useSession } from "./session.js";

/** Soft≠Live Page231: missing contract ops must not crash vault residual. */
export function safeVaultPath(op, opts) {
  try {
    return path(op, opts);
  } catch (_e) {
    if (op === "get__admin_backup_status") return "/admin/backup/status";
    if (op === "get__admin_pg-backup_status" || op === "get__admin_pg_backup_status") {
      return "/admin/pg-backup/status";
    }
    if (op === "get__admin_backup_export") return "/admin/backup/export";
    return "";
  }
}

/** Pure: 64-hex encryption key face — never invent a Soft key. */
export function vaultKeyOkOf(key) {
  return /^[0-9a-fA-F]{64}$/.test(String(key || ""));
}

/** Pure: sa_security_admin / superadmin / full — Live grant only (status mirror). */
export function canExportVaultOf(me) {
  if (!me) return false;
  const r = String(me.role || "");
  if (r === "superadmin") return true;
  const caps = me.caps || me.sa_capabilities || [];
  const list = Array.isArray(caps) ? caps : String(caps || "").split(",");
  return list.some((c) =>
    /sa_security_admin|sa_full_access|sa_full/i.test(String(c || "").trim()),
  );
}

/** Pure: Live DR chip from dual vault + pg-backup posture. */
export function vaultPgChipOf(post, pgPost) {
  const p = post && typeof post === "object" ? post : {};
  const g = pgPost && typeof pgPost === "object" ? pgPost : {};
  const pg = g.pg && typeof g.pg === "object" ? g.pg : {};
  const tools =
    p.pg_backup_tools_on_api != null
      ? !!p.pg_backup_tools_on_api
      : !!pg.tools_on_host;
  const configured =
    p.pg_backup_configured != null
      ? !!p.pg_backup_configured
      : !!pg.configured;
  const armed = p.pg_backup_armed != null ? !!p.pg_backup_armed : !!g.armed;
  if (tools && configured) return { label: "armed+tools", on: "1", tone: "live" };
  if (configured) return { label: "ops image", on: "1", tone: "ops" };
  if (armed) return { label: "flag only", on: "0", tone: "warn" };
  return { label: "off", on: "0", tone: "off" };
}

export function SaVaultDesk({ toast }) {
  const { me } = useSession();
  const posture = useApi(safeVaultPath("get__admin_backup_status"));
  const pgPosture = useApi(safeVaultPath("get__admin_pg-backup_status"));
  // Fail-closed while loading — Soft invent vault seats from client role deleted.
  const post = posture.loading || !posture.data ? {} : (posture.data || {});
  const pg = pgPosture.loading || !pgPosture.data ? {} : (pgPosture.data || {});
  const seat = post.schedule_read_allowed === true;
  const exportArmed = post.export_allowed === true;
  const detail = post.detail || null;
  const eol = !!post.sqlite_physical_eol;
  const pgChip = vaultPgChipOf(post, pg);
  const [key, setKey] = useState("");
  const [busy, setBusy] = useState(false);
  const ok = vaultKeyOkOf(key);

  const reRead = () => {
    posture.reload && posture.reload();
    pgPosture.reload && pgPosture.reload();
    toast && toast("Vault · PG DR posture re-read", "go");
  };

  const exportBackup = async () => {
    if (!seat) {
      toast && toast("Backup export is not available on this seat.", "warn");
      return;
    }
    if (!exportArmed) {
      toast && toast(
        "SQLite export unarmed honesty (EOL) — use CAP-OPS / pg_backup_ops",
        "warn",
      );
      return;
    }
    if (!ok) { toast && toast("64-hex encryption key required", "warn"); return; }
    setBusy(true);
    try {
      const r = await api(safeVaultPath("get__admin_backup_export"), {
        headers: { "X-Backup-Encryption-Key": key },
        raw: true,
        timeoutMs: 120000,
      });
      const blob = await r.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = "sentinel-backup.enc";
      a.click();
      URL.revokeObjectURL(a.href);
      toast && toast("Encrypted backup downloaded", "go");
    } catch (e) {
      const msg = String((e && e.message) || e || "Export refused");
      toast && toast(
        /sqlite_backup_soft_under_eol|409/.test(msg)
          ? "Export refused under EOL — use the PG backup ops image"
          : msg,
        "bad",
      );
    } finally {
      setBusy(false);
    }
  };

  if (!seat) {
    return html`<div className="sa-vault-desk sa-vault-desk--liquid"
      data-cmc-page="page128-sa-vault" data-cmc-legacy="page78-sa-vault"
      data-cmc-residual="page159-vault"
      data-cmc-page2="page231-pg-backup">
      <div className="sa-vault-desk__rail">
        <div>
          <p className="eyebrow">Platform · Backup export</p>
          <h2 className="sa-vault-desk__title">Seat closed</h2>
          <p className="note">Encrypted export stays sealed until this seat opens.</p>
        </div>
        <div className="btn-row sa-vault-desk__acts">
          <button type="button" className="btn btn--ghost btn--sm" onClick=${reRead}
            disabled=${posture.loading || pgPosture.loading}>Re-read posture</button>
          <button type="button" className="btn btn--ghost btn--sm"
            onClick=${() => go("/control/security")}>Security</button>
          <button type="button" className="btn btn--ghost btn--sm"
            onClick=${() => go("/control/backup")}>Backup drill</button>
        </div>
      </div>
      <${Card} title="Vault posture" meta="status">
        ${posture.loading ? html`<p className="note">Reading posture…</p>` : null}
        ${posture.error ? html`<${Alert} tone="warn" title="Posture refused"
          say=${String(posture.error)}/>` : null}
        ${!posture.loading && !posture.error ? html`
          <p className="note">
            export_allowed no · detail ${detail || "vault_seat_gated"}
            · sqlite_eol ${eol ? "yes" : "no"}
            · pg_backup ${pgChip.label}
          </p>` : null}
      <//>
      <${Alert} tone="info" title="Vault export not on this account"
        say=${"Backup export needs superadmin or security-admin. This account is "
          + (me && me.role ? me.role : "operator")
          + " — nothing is invented to paint Soft success. server posture."}/>
      <${Card} title="Encrypted backup export" meta="gated">
        <p className="note">No export write is offered on this grant.</p>
      <//>
    </div>`;
  }

  return html`<div className="sa-vault-desk sa-vault-desk--liquid"
    data-cmc-page="page128-sa-vault" data-cmc-legacy="page78-sa-vault"
    data-cmc-residual="page159-vault"
    data-cmc-page2="page231-pg-backup">
    <div className="sa-vault-desk__rail">
      <div>
        <p className="eyebrow">Platform · Backup export</p>
        <h2 className="sa-vault-desk__title">Encrypted export</h2>
        <p className="note">
          Singular desks — Soft duplicates forbidden. SQLite export is here only.
          PG DR readiness: DR posture when the ops plane carries tools.
          Restore drill and schedule live under Control → Backup.
        </p>
      </div>
      <div className="btn-row sa-vault-desk__acts">
        <button type="button" className="btn btn--ghost btn--sm" onClick=${reRead}
          disabled=${posture.loading || pgPosture.loading}>Re-read</button>
        <button type="button" className="btn btn--go btn--sm"
          onClick=${() => go("/control/security")}>Security desk</button>
        <button type="button" className="btn btn--ghost btn--sm"
          onClick=${() => go("/control/backup")}>Backup schedule & drill</button>
      </div>
    </div>

    <${Card} title="Vault posture" meta="status">
      ${posture.loading ? html`<p className="note">Reading posture…</p>` : null}
      ${posture.error ? html`<${Alert} tone="warn" title="Posture refused"
        say=${String(posture.error)}/>` : null}
      ${!posture.loading && !posture.error ? html`
        <div className="sa-vault-desk__kpis" role="group" aria-label="Vault posture">
          ${[
            ["Seat", seat ? "open" : "gated", seat ? "1" : "0", "status Live"],
            ["SQLite export", exportArmed ? "armed" : (eol ? "EOL" : "—"), exportArmed ? "1" : "0", "file encrypt"],
            ["PG backup", pgChip.label, pgChip.on, "DR honesty"],
            ["Import", "runbook", "0", "Soft import forbidden"],
          ].map(([k, v, on, em]) => html`
            <div key=${k} className="sa-vault-desk__kpi" data-on=${on}>
              <span>${k}</span>
              <b>${v}</b>
              <em>${em}</em>
            </div>`)}
        </div>
        ${detail ? html`<p className="note">detail · ${detail}</p>` : null}
        ${post.pg_backup_note ? html`<p className="note">${post.pg_backup_note}</p>` : null}
        <p className="note">
          export bus follows posture
          · export ${exportArmed ? "open" : "sealed"}
        </p>` : null}
    <//>

    <${Card} title="PG DR readiness" meta="status">
      ${pgPosture.loading ? html`<p className="note">Reading PG DR…</p>` : null}
      ${pgPosture.error ? html`<${Alert} tone="warn" title="PG DR posture refused"
        say=${String(pgPosture.error)}/>` : null}
      ${!pgPosture.loading && !pgPosture.error ? html`
        <div className="sa-vault-desk__kpis" role="group" aria-label="PG DR">
          ${[
            ["Survey", pg.survey_plane_allowed ? "open" : "gated", pg.survey_plane_allowed ? "1" : "0", "seat"],
            ["Configured", (pg.pg && pg.pg.configured) ? "yes" : "no", (pg.pg && pg.pg.configured) ? "1" : "0", "DSN+B2"],
            ["Tools here", (pg.pg && pg.pg.tools_on_host) ? "pg_dump" : "absent", (pg.pg && pg.pg.tools_on_host) ? "1" : "0", "API host"],
            ["Run dump", pg.run_allowed === true ? "open" : "sealed", pg.run_allowed === true ? "1" : "0", "Server only"],
          ].map(([k, v, on, em]) => html`
            <div key=${k} className="sa-vault-desk__kpi" data-on=${on}>
              <span>${k}</span>
              <b>${v}</b>
              <em>${em}</em>
            </div>`)}
        </div>
        ${pg.detail ? html`<p className="note">detail · ${pg.detail}</p>` : null}
        ${pg.pg && pg.pg.note ? html`<p className="note">${pg.pg.note}</p>` : null}
        <p className="note">CLI · ${pg.runbook_cli || "python -m pg_backup_ops"} · secrets never in JSON.</p>` : null}
    <//>

    ${eol ? html`
      <${Alert} tone="warn" title="SQLite file export unarmed honesty"
        say="Physical SQLite export is end-of-life on this host (refused). Backup truth follows DR posture only — dump success is never invented on a thin API."/>`
      : html`<${Alert} tone="info" title="Desks are singular — Soft duplicates forbidden"
        say="Export is here. Restore drill and schedule live under Platform control → Backup. MAC drift, command-key rotation and e-stop live under Security. Never invent a Soft “backed up” or “rotated” toast."/>`}

    <div className="sa-vault-desk__kpis" role="group" aria-label="Vault export KPIs">
      <div className="sa-vault-desk__kpi" data-on=${ok ? "1" : "0"}>
        <span>Key face</span>
        <b>${ok ? "64-hex" : "—"}</b>
        <em>${ok ? "Ready" : "not inventing Soft key"}</em>
      </div>
      <div className="sa-vault-desk__kpi" data-on=${exportArmed ? "1" : "0"}>
        <span>Bus</span>
        <b>${exportArmed ? "Export" : "gated"}</b>
        <em>Export bus</em>
      </div>
      <div className="sa-vault-desk__kpi" data-on="0">
        <span>Import</span>
        <b>Runbook</b>
        <em>Soft import forbidden</em>
      </div>
      <div className="sa-vault-desk__kpi" data-on="1">
        <span>Seat</span>
        <b>${me && me.role === "superadmin" ? "root" : "cap"}</b>
        <em>sa_security_admin Live</em>
      </div>
    </div>

    <${Card} title="Encrypted backup export" meta="status"
        sub="Root SA grants sa_security_admin. The encryption key is never stored — lose it and the archive is unrecoverable.">
      <${Field} label="Encryption key"
          hint=${key && !ok ? "Must be exactly 64 hexadecimal characters." : "64 hex. Held only for this export request."}
          bad=${!!key && !ok}>
        <${Input} icon="key" value=${key} onChange=${(e) => setKey(e.target.value)}
          placeholder="64 hex characters" bad=${!!key && !ok}/>
      <//>
      <div className="btn-row sa-vault-desk__acts">
        <button className="btn btn--ghost btn--sm" disabled=${!ok || busy || !exportArmed}
          onClick=${exportBackup}>
          <${Ico} d="download" w=${13}/> Export</button>
        <button className="btn btn--danger btn--sm" disabled=${true}
          title="Import is runbook-only — Soft import is forbidden">Import (runbook only) · UI</button>
      </div>
      <p className="note">409 under SQLITE_PHYSICAL_EOL is honest refuse — Soft “backed up” invent stays deleted.</p>
    <//>
  </div>`;
}

export function Vault({ toast }) {
  return html`<${F} data-cmc-page="page78-sa-vault-mount">
    <${SaVaultDesk} toast=${toast}/>
  <//>`;
}
